import { biEvents } from 'api/bi-events';
import { StepLayout } from 'components/StepLayout/StepLayout';
import { BI_ACTION_NAME, BI_STEP_NAME } from 'config/bi';
import { isExistingSiteEntryMode } from 'config/entry-mode';
import { StepId } from 'config/steps';
import { useProductCatalog } from 'hooks/useProductCatalog';
import { useSyncProductSelection } from 'hooks/useSyncProductSelection';
import React, { FC } from 'react';
import { useTranslation } from '@wix/wix-i18n-config';
import { useAppStore } from 'stores/appStore';
import { SelectableProduct } from 'types/selectable-product';
import { SelectProductsStepOptions } from './components/SelectProductsStepOptions';
import { getSelectedProductIds } from 'utils/product-selection';

function formatContinueBiValue(selectedProducts: SelectableProduct[], productsSource?: string): string {
  const productIds = getSelectedProductIds(selectedProducts).join(',');

  if (!productsSource) {
    return productIds;
  }

  return `${productIds}|source:${productsSource}`;
}

export const SelectProductsStep: FC = () => {
  const { t } = useTranslation();
  const selectedProducts = useAppStore((state) => state.selectedProducts);
  const entryMode = useAppStore((state) => state.entryMode);
  const isExistingSiteMode = isExistingSiteEntryMode(entryMode);
  const { products, isReady, productsSource } = useProductCatalog();
  useSyncProductSelection(products, isReady);

  return (
    <StepLayout
      title={t(isExistingSiteMode ? 'select-products-step.existing-site.title' : 'select-products-step.title')}
      description={t(
        isExistingSiteMode ? 'select-products-step.existing-site.description' : 'select-products-step.description',
      )}
      nextButtonProps={{
        label: t('select-products-step.actions.primary-button'),
        disabled: !selectedProducts.length || !isReady,
      }}
      onBack={() => {
        biEvents.actionClicked({
          screenName: BI_STEP_NAME[StepId.SelectProducts],
          action: BI_ACTION_NAME.BACK,
        });
      }}
      onNext={() => {
        biEvents.actionClicked({
          screenName: BI_STEP_NAME[StepId.SelectProducts],
          value: formatContinueBiValue(selectedProducts, isExistingSiteMode ? productsSource : undefined),
          action: BI_ACTION_NAME.CONTINUE,
        });
      }}
    >
      <SelectProductsStepOptions />
    </StepLayout>
  );
};
